/* global google */
import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@material-tailwind/react";
import { MapPin, X, } from "lucide-react";
import Message from "../common/Message";
import Onboarding from "../common/Onboarding";
import CustomInput from "../common/CustomInput";
import Map from "../common/Map";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash/debounce";
import { useGoogleMaps } from "../../hooks/useGoogleMaps";

import { getSuggestionStatus } from "../../Redux/businessSlice";
import BusinessConversionFlow from "./BusinessConversionFlow";
import SaveLocationConfirmModal from "./SaveLocationConfirmModal";

const getCurrentTime = () => {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export default function PickupFlowScreen({
  onOpenSavedLocations,
  messages,
  setMessages,
  pickupLocation,
  setPickupLocation,
  deliveryLocation,
  setDeliveryLocation,
  onSelectPickup,
  darkMode,
  toggleDarkMode,
  isEditing,
  editingField,
  currentOrder,
  onEditComplete,
  onMore,
  showBack,
  onBack, showMore
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [phoneNumberInput, setPhoneNumberInput] = useState("");
  const [showMap, setShowMap] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showLocationButtons, setShowLocationButtons] = useState(true);
  const [pickupPhoneNumber, setPickupPhoneNumber] = useState("");
  const [dropoffPhoneNumber, setDropoffPhoneNumber] = useState("");
  const [currentStep, setCurrentStep] = useState("pickup-location");
  const [pendingPlace, setPendingPlace] = useState(null);
  const [showCustomInput, setShowCustomInput] = useState(true);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [pickupItems, setPickupItems] = useState("");
  const [placesLibrary, setPlacesLibrary] = useState(null);
  const [showConversionFlow, setShowConversionFlow] = useState(false);

  // Search states
  const [isSearching, setIsSearching] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [searchError, setSearchError] = useState(null);

  const dispatch = useDispatch();
  const listRef = useRef(null);
  const timeoutRef = useRef(null);
  const deliveryLocationRef = useRef(null);
  const pickupLocationRef = useRef(null);
  const currentUser = useSelector(s => s.auth.user);
  const prevStepRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const usedButtonIdsRef = useRef(new Set());
  const pickupPhoneNumberRef = useRef("");

  const pickupCoordinatesRef = useRef(null);
  const deliveryCoordinatesRef = useRef(null);
  const pickupPhoneMatchesUserPhone = useRef(false);
  const sessionTokenRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const pendingDeliveryButtonIdRef = useRef(null);
  const { isLoaded } = useGoogleMaps();

  // autoscroll
  useEffect(() => {
    if (listRef.current) {
      const scrollToBottom = () => {
        requestAnimationFrame(() => {
          listRef.current.scrollTop = listRef.current.scrollHeight;
        });
      };

      const timeoutId = setTimeout(scrollToBottom, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [messages, showCustomInput, showPhoneInput, currentStep, predictions.length]);

  useEffect(() => {
    console.log('[PICKUP SCREEN Debug] State changed:', {
      currentStep,
      pickupPhoneNumber,
      deliveryLocation,
      pickupItems,
      messagesLength: messages.length,
      lastMessage: messages[messages.length - 1]?.text
    });
  }, [currentStep, pickupPhoneNumber, deliveryLocation, pickupItems, messages]);

  useEffect(() => {
    if (!window.google) return;

    const initPlaces = async () => {
      try {
        const { AutocompleteSuggestion, Place } = await google.maps.importLibrary("places");
        setPlacesLibrary({ AutocompleteSuggestion, Place });
      } catch (error) {
        console.error('Failed to load Places library:', error);
      }
    };

    initPlaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.google]);

  const getSessionToken = () => {
    if (!sessionTokenRef.current && window.google) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  };

  const searchPlaces = useCallback(async (query, step) => {
    if (!query || query.length < 2 || !window.google || !placesLibrary) {
      setPredictions([]);
      return;
    }
    if (step !== "pickup-location" && step !== "delivery-location") {
      setPredictions([]);
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    const requestId = ++searchRequestIdRef.current;

    try {
      const { AutocompleteSuggestion } = placesLibrary;

      const request = {
        input: query,
        locationBias: {
          radius: 50000,
          center: { lat: 6.5244, lng: 3.3792 },
        },
        includedRegionCodes: ['ng'],
        includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'establishment'],
        sessionToken: getSessionToken(),
      };

      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      if (requestId !== searchRequestIdRef.current) return;

      const formattedPredictions = (suggestions || []).map((s) => {
        const p = s.placePrediction;
        return {
          place_id: p.placeId,
          description: p.text.text,
          structured_formatting: {
            main_text: p.mainText?.text || p.text.text,
            secondary_text: p.secondaryText?.text || '',
          },
          _placePrediction: p, // keep the original for toPlace()
        };
      });

      setPredictions(formattedPredictions);
      setIsSearching(false);

    } catch (error) {
      if (requestId !== searchRequestIdRef.current) return;
      console.error('AutocompleteSuggestion failed:', error);
      setSearchError('Location search failed. Please try again.');
      setPredictions([]);
      setIsSearching(false);
    }
  }, [placesLibrary]);

  const searchPlacesRef = useRef(searchPlaces);
  useEffect(() => {
    searchPlacesRef.current = searchPlaces;
  }, [searchPlaces]);

  const debouncedSearch = useRef(
    debounce((query, step) => {
      if (step !== "pickup-location" && step !== "delivery-location") {
        setPredictions([]);
        setIsSearching(false);
        return;
      }
      searchPlacesRef.current(query, step);
    }, 400)
  ).current;

  useEffect(() => {
    debouncedSearch(searchTerm, currentStep);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, currentStep, debouncedSearch]);

  useEffect(() => {
    // Clear search whenever step changes, regardless of what it changes to
    if (prevStepRef.current !== currentStep) {
      setSearchTerm("");
      setPredictions([]);
      setIsSearching(false);
      sessionTokenRef.current = null;
      prevStepRef.current = currentStep;
    }
  }, [currentStep]);

  // FIXED: Reset messages when entering edit mode
  useEffect(() => {
    if (isEditing && editingField) {
      // Reset messages to show only the relevant question
      switch (editingField) {
        case "pickup-location":
          setCurrentStep("pickup-location");
          setShowCustomInput(true);
          setShowPhoneInput(false);
          setShowLocationButtons(true);
          setMessages([
            {
              id: Date.now(),
              from: "them",
              text: "Which location do you want to pickup from?",
              time: getCurrentTime(),
              status: "delivered"
            }
          ]);
          break;
        case "delivery-location":
          setCurrentStep("delivery-location");
          setShowCustomInput(true);
          setShowPhoneInput(false);
          setShowLocationButtons(true);
          const editDeliveryId = Date.now();
          pendingDeliveryButtonIdRef.current = editDeliveryId;
          setMessages([
            {
              id: editDeliveryId,
              from: "them",
              text: "Set your delivery location. Choose Delivery Location",
              time: getCurrentTime(),
              status: "delivered",
              hasChooseDeliveryButton: true,
              hasViewSavedLocations: true,
            }
          ]);
          break;
        case "pickup-items":
          setCurrentStep("pickup-items");
          setShowCustomInput(true);
          setShowPhoneInput(false);
          setShowLocationButtons(false);
          setMessages([
            {
              id: Date.now(),
              from: "them",
              text: "What item(s) do you want to pick up?",
              time: getCurrentTime(),
              status: "delivered"
            }
          ]);
          break;
        case "pickup-phone":
          setCurrentStep("pickup-phone");
          setShowPhoneInput(true);
          setShowCustomInput(false);
          setShowLocationButtons(false);
          setMessages([
            {
              id: Date.now(),
              from: "them",
              text: "Please enter pick up phone number Use My Phone Number",
              time: getCurrentTime(),
              status: "delivered",
              hasUseMyNumberButton: true,
              phoneNumberType: "pickup",
            }
          ]);
          break;
        case "dropoff-phone":
          setCurrentStep("dropoff-phone");
          setShowPhoneInput(true);
          setShowCustomInput(false);
          setShowLocationButtons(false);
          setMessages([
            {
              id: Date.now(),
              from: "them",
              text: "Kindly enter drop off phone number Use My Phone Number",
              time: getCurrentTime(),
              status: "delivered",
              // hasUseMyNumberButton: true,
              phoneNumberType: "dropoff",
            }
          ]);
          break;
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, editingField]);

  const handleSuggestionSelect = (prediction) => {
    if (!window.google || isSubmittingRef.current) return;

    if (!prediction.place_id || typeof prediction.place_id !== 'string') {
      console.error('Invalid place_id:', prediction.place_id);
      setSearchError('Invalid location. Please try again.');
      setShowCustomInput(true);
      isSubmittingRef.current = false;
      return;
    }

    isSubmittingRef.current = true;
    setShowCustomInput(false);
    console.log('[selected prediction]', prediction.place_id, prediction.description);

    const handleLocationResult = (result) => {
      const { lat, lng, address, name } = result;
      const locationText = prediction.description;

      if (currentStep === "pickup-location") {
        pickupCoordinatesRef.current = { lat, lng };
        send(locationText, "pickup-location");
      } else if (currentStep === "delivery-location") {
        deliveryCoordinatesRef.current = { lat, lng };
        send(locationText, "delivery");
      }

      setSelectedPlace({ name, address, lat, lng });
      setSearchTerm(name);
      setPredictions([]);
      isSubmittingRef.current = false;
    };

    if (prediction.lat && prediction.lng) {
      handleLocationResult({
        lat: prediction.lat,
        lng: prediction.lng,
        address: prediction.description,
        name: prediction.structured_formatting?.main_text || prediction.description,
      });
      return;
    }

    const place = prediction._placePrediction.toPlace();

    place.fetchFields({
      fields: ['geometry', 'formattedAddress', 'displayName'],
    })
      .then((result) => {
        handleLocationResult({
          lat: result.place.location.lat(),
          lng: result.place.location.lng(),
          address: result.place.formattedAddress || prediction.description,
          name: result.place.displayName || prediction.structured_formatting?.main_text || prediction.description,
        });
      })
      .catch((error) => {
        console.error('Place fetch failed:', error);
        fallbackToGeocoder(prediction.place_id, prediction.description);
      });

    // final fallback
    const fallbackToGeocoder = (placeId, description) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ placeId: placeId }, (results, status) => {
        if (status === 'OK' && results[0]) {
          handleLocationResult({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
            address: results[0].formatted_address || description,
            name: results[0].formatted_address || description,
          });
        } else {
          isSubmittingRef.current = false;
          setShowCustomInput(true);
          setSearchError('Could not find this location. Please try again.');
        }
      });
    };
  };


  const initialMessages = [
    { id: 1, from: "them", text: "Which location do you want to pickup from?", time: getCurrentTime(), status: "delivered" },
  ];


  useEffect(() => {
    if (messages.length === 0) {
      setMessages(initialMessages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, setMessages]);

  const handleMapSelect = (place) => {
    setSelectedPlace(place);
  };

  const handleMapSelection = () => {
    if (!selectedPlace) return;
    setPendingPlace(selectedPlace);
  };

  const proceedWithLocation = (place) => {
    const locationText = place.name || place.address;

    if (currentStep === "pickup-location") {
      pickupCoordinatesRef.current = { lat: place.lat, lng: place.lng };
      setPickupLocation(locationText);
      pickupLocationRef.current = locationText;
      send(locationText, "pickup-location");
    } else if (currentStep === "delivery-location") {
      deliveryCoordinatesRef.current = { lat: place.lat, lng: place.lng };
      setDeliveryLocation(locationText);
      deliveryLocationRef.current = locationText;
      send(locationText, "delivery");

      if (pendingDeliveryButtonIdRef.current) {
        const usedId = pendingDeliveryButtonIdRef.current;
        setMessages((prev) => prev.map((msg) =>
          msg.id === usedId ? { ...msg, hasChooseDeliveryButton: false } : msg
        ));
        usedButtonIdsRef.current.add(usedId);
        pendingDeliveryButtonIdRef.current = null;
      }
    }

    setShowMap(false);
    setSelectedPlace(null);
    setPendingPlace(null);
    setSearchTerm("");
    setPredictions([]);
  };

  const geocodeAddress = (address, type) => {
    if (!window.google) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { address: address + ', Lagos, Nigeria', componentRestrictions: { country: 'ng' } },
      (results, status) => {
        if (status === 'OK' && results[0]) {
          const lat = results[0].geometry.location.lat();
          const lng = results[0].geometry.location.lng();

          if (type === "pickup-location") {
            pickupCoordinatesRef.current = { lat, lng };
            send(address, "pickup-location");
          } else {
            deliveryCoordinatesRef.current = { lat, lng };
            send(address, "delivery");
          }
        }
      }
    );
  };

  const handleLocationSelectedFromSaved = (location, type) => {
    const locationText = location.address || location.name;

    // use the lat/lng directly
    if (currentStep === "pickup-location") {
      if (location.lat && location.lng) {
        pickupCoordinatesRef.current = { lat: location.lat, lng: location.lng };
        setPickupLocation(locationText);
        pickupLocationRef.current = locationText;
        send(locationText, "pickup-location");
      } else {
        // If no coordinates, geocode the address
        geocodeAddress(locationText, "pickup-location");
      }
    } else if (currentStep === "delivery-location") {
      if (location.lat && location.lng) {
        deliveryCoordinatesRef.current = { lat: location.lat, lng: location.lng };
        setDeliveryLocation(locationText);
        deliveryLocationRef.current = locationText;
        send(locationText, "delivery");
      } else {
        geocodeAddress(locationText, "delivery");
      }

      if (pendingDeliveryButtonIdRef.current) {
        const usedId = pendingDeliveryButtonIdRef.current;
        setMessages((prev) => prev.map((msg) =>
          msg.id === usedId ? { ...msg, hasChooseDeliveryButton: false } : msg
        ));
        usedButtonIdsRef.current.add(usedId);
        pendingDeliveryButtonIdRef.current = null;
      }
    }
  };



  const checkAndShowSuggestion = async () => {
    try {
      const suggestionResult = await dispatch(getSuggestionStatus()).unwrap();
      if (suggestionResult?.shouldSuggest) {
        setTimeout(() => {
          setMessages(prev => [...prev, {
            id: Date.now() + 10,
            from: "them",
            text: `🚀 You've used Sendrey ${suggestionResult.monthlyTaskCount} times this month! Upgrade to a Business Account to unlock team access, expense reports & scheduled deliveries.`,
            time: getCurrentTime(),
            status: "delivered",
            isBusinessSuggestion: true,
            onBusinessSuggestionAccept: () => setShowConversionFlow(true),
          }]);
        }, 2000);
      }
    } catch (err) {
      // fail silently — suggestion is non-critical
    }
  };

  // validate 
  const validateField = (text, source) => {
    const trimmed = text.trim();

    if (source === "pickup-location" || source === "delivery") {
      if (!trimmed || trimmed.length < 3)
        return "Invalid answer. Input must be a valid location (at least 3 characters).";
      if (!/[a-zA-Z]/.test(trimmed))
        return "Invalid answer. Input must be a text location.";
      // Must have at least 2 consecutive letters (catches "l;[", "a,b", "1a!", etc.)
      if (!/[a-zA-Z]{2,}/.test(trimmed))
        return "Invalid answer. Input must be a valid location name.";
      return null;
    }

    if (source === "pickup-items") {
      if (!trimmed || trimmed.length < 2)
        return "Invalid answer. Input must be a text description of your item(s).";
      if (/^\d+$/.test(trimmed))
        return "Invalid answer. Input must be text describing the item(s), not just a number.";
      return null;
    }

    if (source === "pickup-phone" || source === "dropoff-phone") {
      const phonePattern = /^(\+?[\d\s\-()+]{7,15})$/;
      if (!phonePattern.test(trimmed))
        return "Invalid answer. Input must be a valid phone number.";
      return null;
    }

    return null;
  };

  const send = (text, source) => {
    console.log('[PICKUP SCREEN Debug send] called with:', { text, source, currentStep, pickupPhoneNumber, deliveryLocation });

    if (!text || typeof text !== "string") return;
    isSubmittingRef.current = false;

    // ── Field validation ──────────────────────────────────────
    const validationError = validateField(text, source);
    if (validationError) {
      const newMsg = {
        id: Date.now(),
        from: "me",
        text: text.trim(),
        time: getCurrentTime(),
        status: "sent",
      };
      setMessages((prev) => [...prev, newMsg]);

      const inProgress = { id: Date.now() + 1, from: "them", text: "In progress...", status: "delivered" };
      setMessages((p) => [...p, inProgress]);

      setTimeout(() => {
        setMessages((prev) => prev.filter((m) => m.text !== "In progress..."));
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 2,
            from: "them",
            text: validationError,
            time: getCurrentTime(),
            status: "delivered",
          },
        ]);

        // Re-show the correct input for the failed step
        if (source === "pickup-location" || source === "delivery") {
          setShowCustomInput(true);
          setShowLocationButtons(true);
        } else if (source === "pickup-items") {
          setShowCustomInput(true);
        } else if (source === "pickup-phone" || source === "dropoff-phone") {
          setShowPhoneInput(true);
          setPhoneNumberInput("");
        }
      }, 1200);

      return; // stop normal flow
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const msgText = text.trim();
    setShowLocationButtons(false);

    if (source === "pickup-location") {
      pickupLocationRef.current = msgText;
      setPickupLocation(msgText);
      // console.log('Stored typed pickup(s) location:', msgText);
    } else if (source === "delivery") {
      deliveryLocationRef.current = msgText;
      setDeliveryLocation(msgText);
      // console.log('Stored typed delivery location pickup:', msgText);
    }

    const newMsg = {
      id: Date.now(),
      from: "me",
      text: msgText,
      time: getCurrentTime(),
      status: "sent",
    };
    setMessages((prev) => [...prev, newMsg]);

    setShowCustomInput(false);
    setShowPhoneInput(false);

    const botResponse = {
      id: Date.now() + 1,
      from: "them",
      text: "In progress...",
      status: "delivered",
    };
    setMessages((p) => [...p, botResponse]);

    timeoutRef.current = setTimeout(() => {
      setMessages((prev) => prev.filter((msg) => msg.text !== "In progress..."));

      //  edit
      if (isEditing && onEditComplete) {
        if (editingField === "pickup-location" && source === "pickup-location") {
          const updatedData = {
            ...currentOrder,
            pickupLocation: msgText,
            pickupCoordinates: pickupCoordinatesRef.current
          };
          onEditComplete(updatedData);
          return;
        }

        if (editingField === "delivery-location" && source === "delivery") {
          const updatedData = {
            ...currentOrder,
            deliveryLocation: msgText,
            deliveryCoordinates: deliveryCoordinatesRef.current
          };
          onEditComplete(updatedData);
          return;
        }

        if (editingField === "pickup-phone" && source === "pickup-phone") {
          let formattedNumber = text;
          if (text.startsWith('+234')) {
            // Remove accidental 0 after country code
            formattedNumber = text.replace(/^\+2340/, '+234');
          } else if (!text.startsWith('+234') && text.replace(/\D/g, '').length === 11) {
            formattedNumber = `+234${text.substring(1)}`;
          }
          const updatedData = {
            ...currentOrder,
            pickupPhone: formattedNumber
          };
          onEditComplete(updatedData);
          return;
        }

        if (editingField === "dropoff-phone" && source === "dropoff-phone") {
          let formattedNumber = text;
          if (text.startsWith('+234')) {
            // Remove accidental 0 after country code
            formattedNumber = text.replace(/^\+2340/, '+234');
          } else if (!text.startsWith('+234') && text.replace(/\D/g, '').length === 11) {
            formattedNumber = `+234${text.substring(1)}`;
          }
          const updatedData = {
            ...currentOrder,
            dropoffPhone: formattedNumber
          };
          onEditComplete(updatedData);
          return;
        }

        if (editingField === "pickup-items" && source === "pickup-items") {
          const updatedData = {
            ...currentOrder,
            pickupItems: msgText
          };
          onEditComplete(updatedData);
          return;
        }
      }

      // NORMAL FLOW - Only run if not in edit mode
      if (!isEditing) {
        if ((source === "pickup-phone" || source === "dropoff-phone")) {
          const isUseMyNumber = text.startsWith('+234');

          if (!isUseMyNumber) {
            const error = validatePhone(text);
            if (error) {
              setMessages((p) => [
                ...p,
                {
                  id: Date.now() + 2,
                  from: "them",
                  text: error,
                  time: getCurrentTime(),
                  status: "delivered",
                },
              ]);

              setPhoneNumberInput("");
              setShowPhoneInput(true);
              return;
            }
          }
        }

        if (source === "pickup-location" && !pickupItems) {
          setMessages((p) => [
            ...p,
            {
              id: Date.now() + 2,
              from: "them",
              text: "What item(s) do you want to pick up?",
              time: getCurrentTime(),
              status: "delivered",
            },
          ]);
          setCurrentStep("pickup-items");
          setShowCustomInput(true);

        } else if (source === "pickup-items" && !pickupPhoneNumber) {
          setPickupItems(msgText);
          setMessages((p) => [
            ...p,
            {
              id: Date.now() + 2,
              from: "them",
              text: "Please enter pick up phone number Use My Phone Number",
              time: getCurrentTime(),
              status: "delivered",
              hasUseMyNumberButton: true,
              phoneNumberType: "pickup",
            },
          ]);
          setCurrentStep("pickup-phone");
          setShowPhoneInput(true);
          setShowCustomInput(false);
        } else if (source === "pickup-phone") {
          let formattedNumber = text;
          if (text.startsWith('+234')) {
            // Remove accidental 0 after country code
            formattedNumber = text.replace(/^\+2340/, '+234');
          } else if (!text.startsWith('+234') && text.replace(/\D/g, '').length === 11) {
            formattedNumber = `+234${text.substring(1)}`;
          }

          setPickupPhoneNumber(formattedNumber);
          pickupPhoneNumberRef.current = formattedNumber;

          // ← Check if pickup phone matches user's phone
          const myNumber = currentUser?.phone || currentUser?.user?.phone;
          const normalizedMyNumber = myNumber?.replace(/^\+2340/, '+234');
          pickupPhoneMatchesUserPhone.current = formattedNumber === normalizedMyNumber;

          setMessages((prev) => prev.map((msg) =>
            msg.hasUseMyNumberButton ? { ...msg, hasUseMyNumberButton: false } : msg
          ));

          const deliveryPromptId = Date.now() + 2;
          pendingDeliveryButtonIdRef.current = deliveryPromptId;

          setMessages((prev) => prev.filter(msg => msg.text !== "In progress..."));

          setTimeout(() => {
            setMessages((p) => [
              ...p,
              {
                id: deliveryPromptId,
                from: "them",
                text: "Set your delivery location. Choose Delivery Location",
                time: getCurrentTime(),
                status: "delivered",
                hasChooseDeliveryButton: true,
                hasViewSavedLocations: true,
                disableUseMyNumber: pickupPhoneMatchesUserPhone.current
              },
            ]);
            setCurrentStep("delivery-location");
            setShowCustomInput(true);
            setTimeout(() => setShowLocationButtons(true), 200);
          }, 100);

        } else if (source === "delivery" && !dropoffPhoneNumber) {
          setMessages((p) => [
            ...p,
            {
              id: Date.now() + 2,
              from: "them",
              text: "Kindly enter drop off phone number Use My Phone Number",
              time: getCurrentTime(),
              status: "delivered",
              hasUseMyNumberButton: true,
              phoneNumberType: "dropoff",
              disableUseMyNumber: pickupPhoneMatchesUserPhone.current,
            },
          ]);
          setCurrentStep("dropoff-phone");
          setShowPhoneInput(true);
        } else if (source === "dropoff-phone") {
          let formattedNumber = text;
          if (text.startsWith('+234')) {
            // Remove accidental 0 after country code
            formattedNumber = text.replace(/^\+2340/, '+234');
          } else if (!text.startsWith('+234') && text.replace(/\D/g, '').length === 11) {
            formattedNumber = `+234${text.substring(1)}`;
          }

          setMessages((prev) => prev.map((msg) =>
            msg.hasUseMyNumberButton ? { ...msg, hasUseMyNumberButton: false } : msg
          ));

          const normalizedPickup = pickupPhoneNumberRef.current.startsWith('+234')
            ? pickupPhoneNumberRef.current.replace(/^\+2340/, '+234')
            : pickupPhoneNumberRef.current;

          if (formattedNumber === normalizedPickup) {
            // Show warning but proceed normally
            setMessages((p) => [
              ...p,
              {
                id: Date.now() + 2,
                from: "them",
                text: "Pickup phone and delivery phone cannot be the same. Kindly enter a valid delivery phone.",
                time: getCurrentTime(),
                status: "delivered",
              },
            ]);
            setDropoffPhoneNumber(formattedNumber);
            setPhoneNumberInput("");
            setShowPhoneInput(true);
            return; // ← don't proceed to onSelectPickup, let them re-enter
          }

          setDropoffPhoneNumber(formattedNumber);

          setMessages((prev) => prev.filter(msg => msg.text !== "In progress..."));

          console.log('Final pickup location ref:', pickupLocationRef.current);
          console.log('Final delivery location ref:', deliveryLocationRef.current);
          console.log('Pickup phone ref:', pickupPhoneNumberRef.current);
          console.log('Dropoff phone:', formattedNumber);

          onSelectPickup({
            serviceType: "pick-up",
            pickupLocation: pickupLocationRef.current,
            deliveryLocation: deliveryLocationRef.current,
            pickupPhone: pickupPhoneNumberRef.current,
            dropoffPhone: formattedNumber,
            pickupItems: pickupItems,
            pickupCoordinates: pickupCoordinatesRef.current,
            deliveryCoordinates: deliveryCoordinatesRef.current,
            userId: currentUser?._id
          });

          // call business suggestion
          checkAndShowSuggestion();
        }
      }
    }, 1200);
  };

  const handleUseMyNumber = (messageId, phoneType) => {

    if (usedButtonIdsRef.current.has(messageId)) return;
    usedButtonIdsRef.current.add(messageId);

    setMessages((prev) => prev.map((msg) =>
      msg.id === messageId ? { ...msg, hasUseMyNumberButton: false } : msg
    ));

    const myNumber = currentUser?.phone || currentUser?.user?.phone;

    if (!myNumber) {
      console.error("Phone number not found in user data");

      const errorMessage = {
        id: Date.now(),
        from: "them",
        text: "Oops! Something went wrong. seems you aren't authenticated. Try logging out and logging in again",
        time: getCurrentTime(),
        status: "delivered",
      };

      setMessages((prev) => [...prev, errorMessage]);
      return;
    }

    setMessages((prev) => prev.map((msg) =>
      msg.id === messageId ? { ...msg, hasUseMyNumberButton: false } : msg
    ));

    const formattedNumber = myNumber.startsWith('+234')
      ? myNumber.replace(/^\+2340/, '+234')
      : myNumber;


    if (phoneType === "pickup") {
      setPickupPhoneNumber(formattedNumber);
      pickupPhoneNumberRef.current = formattedNumber;
      send(myNumber, "pickup-phone");
    } else {
      send(myNumber, "dropoff-phone");
    }
  };

  const handleChooseDeliveryClick = (messageId) => {
    pendingDeliveryButtonIdRef.current = messageId;
    setSelectedPlace(null);
    setShowMap(true);
    setShowLocationButtons(true);
  };

  const validatePhone = (phone) => {
    const clean = phone.replace(/\D/g, '');
    if (phone.startsWith('+234')) {
      // Strip accidental leading 0 after country code: +23408... → +2348...
      const normalised = phone.replace(/^\+2340/, '+234');
      const cleanNorm = normalised.replace(/\D/g, '');

      if (cleanNorm.length !== 13) return "Invalid +234 format number";
      if (!['80', '81', '70', '90', '91'].includes(cleanNorm.substring(3, 5))) {
        return "Invalid phone number prefix";
      }
      return null;
    }

    if (clean.length !== 11) return "Invalid Phone Number. Phone number must be 11 digits";
    if (!clean.startsWith('0')) return "Invalid Phone Number. Phone number must start with 0";
    if (!['080', '081', '070', '090', '091'].includes(clean.substring(0, 3))) {
      return "Invalid phone number prefix";
    }
    return null;
  };

  const getSearchPlaceholder = () => {
    if (currentStep === "pickup-location") {
      return "Search for pickup location...";
    } else if (currentStep === "delivery-location") {
      return "Search for delivery location...";
    } else if (currentStep === "pickup-items") {
      return "Enter item(s) to pick up..."
    }
    return "Search for a location...";
  };

  const handleSearchAction = async () => {
    if (!searchTerm.trim() || isSubmittingRef.current) return;

    const text = searchTerm.trim();

    // Non-location step — send directly
    if (currentStep === 'pickup-items') {
      send(text, 'pickup-items');
      setSearchTerm('');
      isSubmittingRef.current = false;
      return;
    }

    if (!isLoaded || !window.google) {
      setSearchError('Google Maps is still loading. Please wait a moment and try again.');
      return;
    }

    isSubmittingRef.current = true;
    setShowCustomInput(false);

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      {
        address: text + ', Lagos, Nigeria',
        componentRestrictions: { country: 'ng' },
      },
      (results, status) => {
        if (status === 'OK' && results[0]) {
          const lat = results[0].geometry.location.lat();
          const lng = results[0].geometry.location.lng();

          if (currentStep === 'pickup-location') {
            pickupCoordinatesRef.current = { lat, lng };
            setPickupLocation(text);
            pickupLocationRef.current = text;
            send(text, 'pickup-location');
          } else if (currentStep === 'delivery-location') {
            deliveryCoordinatesRef.current = { lat, lng };
            setDeliveryLocation(text);
            deliveryLocationRef.current = text;
            send(text, 'delivery');
          }

          setSearchTerm('');
          setPredictions([]);
          setSearchError(null);
        } else {
          setSearchError('Could not find that location. Try being more specific or use the map or suggestions.');
          setShowCustomInput(true);
          isSubmittingRef.current = false;
        }
      }
    );
  };

  if (showMap) {
    return (
      <Onboarding darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
        <div className="w-full h-full mx-auto flex flex-col overflow-hidden max-w-2xl">
          <div className="flex items-center justify-between p-4 bg-inherit border-b">
            <Button
              variant="text"
              onClick={() => {
                setShowMap(false);
                setShowLocationButtons(true);
                setSelectedPlace(null);
                pendingDeliveryButtonIdRef.current = null;
              }}
              className="flex items-center text-red-600 hover:bg-red-600/20"
            >
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
            <Button
              onClick={handleMapSelection}
              disabled={!selectedPlace}
              className={`${!selectedPlace ? "opacity-50 cursor-not-allowed" : "bg-primary hover:bg-primary/90"} flex items-center gap-2 text-white`}
            >
              Select Location
            </Button>
          </div>

          <Map onLocationSelect={handleMapSelect} />

          {selectedPlace && (
            <div className="p-4 bg-white dark:bg-gray-800 border-t">
              <div className="bg-blue-50 dark:bg-blue-900 p-3 rounded-lg">
                <p className="font-semibold text-blue-800 dark:text-blue-200">Selected Location:</p>
                <p className="text-blue-600 dark:text-blue-300">
                  {selectedPlace.name || selectedPlace.address}
                </p>
              </div>
            </div>
          )}

          {pendingPlace && (
            <SaveLocationConfirmModal
              place={pendingPlace}
              darkMode={darkMode}
              onConfirm={proceedWithLocation}
              onCancel={() => setPendingPlace(null)}
            />
          )}
        </div>
      </Onboarding>
    );
  }

  if (showConversionFlow) {
    return (
      <BusinessConversionFlow
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onMore={onMore}
        onComplete={() => setShowConversionFlow(false)}
      />
    );
  }

  return (
    <Onboarding darkMode={darkMode} toggleDarkMode={toggleDarkMode} onMore={onMore} showMore={showMore} showBack={showBack} onBack={onBack}>
      <div className="flex flex-col h-screen">
        <div className="flex-1 overflow-y-auto marketSelection" ref={listRef}>
          <div>
            <div className="min-h-full max-w-3xl mx-auto p-3">
              {messages.map((m) => (
                <p className="mx-auto" key={m.id}>
                  <Message
                    m={m}
                    showCursor={false}
                    showStatusIcons={false}
                    onChooseDeliveryClick={m.hasChooseDeliveryButton ? () => handleChooseDeliveryClick(m.id) : undefined}
                    onUseMyNumberClick={m.hasUseMyNumberButton ? () => handleUseMyNumber(m.id, m.phoneNumberType) : undefined}
                    onViewSavedLocations={m.hasViewSavedLocations ? () => {
                      if (usedButtonIdsRef.current.has(m.id)) return;
                      usedButtonIdsRef.current.add(m.id);


                      setMessages((prev) => prev.map((msg) =>
                        msg.id === m.id ? { ...msg, hasViewSavedLocations: false } : msg
                      ));

                      onOpenSavedLocations(
                        true,
                        handleLocationSelectedFromSaved,
                        () => setShowLocationButtons(true)
                      );
                    } : undefined}
                    disableContextMenu={true}
                  />
                </p>
              ))}

              <div className={`space-y-1 -mt-1 ${currentStep === "delivery-location" ? '-mt-3 pb-5' : ''}`}>
                {showLocationButtons && (currentStep === "pickup-location" || currentStep === "delivery-location") && !showPhoneInput && (
                  <div className="flex flex-col items-start">
                    {currentStep === "pickup-location" && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="text"
                          className="flex items-center py-2 px-4 text-left text-gray-800 dark:text-gray-300"
                          onClick={() => {
                            setSelectedPlace(null);
                            setShowMap(true);
                            setShowLocationButtons(false);
                          }}
                        >
                          <MapPin className="h-4 w-4 mr-2 text-gray-800 dark:text-gray-300" />
                          Find on map
                        </Button>
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <Button
                        variant="text"
                        className="flex items-center py-2 px-4 text-left text-primary"
                        onClick={() => {
                          onOpenSavedLocations(
                            true,
                            handleLocationSelectedFromSaved,
                            () => setShowLocationButtons(true)
                          );
                        }}
                      >
                        View saved locations
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="h-48 sm:h-40 lg:h-40 pb-48"></div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 w-full px-4 sm:px-8 lg:px-28 py-3 bg-gray-100 dark:bg-black-200 z-10">
          {showCustomInput && !showPhoneInput && (
            currentStep === "pickup-location" ||
            currentStep === "delivery-location" ||
            currentStep === "pickup-items"
          ) && (
              <div className="max-w-3xl mx-auto relative">
                <CustomInput
                  countryRestriction="us"
                  stateRestriction="ny"
                  showMic={false}
                  showIcons={false}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={getSearchPlaceholder()}
                  send={handleSearchAction}
                  className="placeholder:text-gray-800 dark:placeholder:text-gray-300"
                />

                {searchTerm.length >= 2 &&
                  (currentStep === "pickup-location" || currentStep === "delivery-location") &&
                  (predictions.length > 0 || isSearching) && (
                    <div className="absolute bottom-full mb-8 left-0 right-0 bg-gray-100 dark:bg-black-200 rounded-lg max-h-60 overflow-y-auto z-50">
                      {isSearching ? (
                        <div className="p-3 text-center text-sm text-gray-900 dark:text-white">Searching...</div>
                      ) : predictions.length === 0 ? (
                        <div className="p-3 text-center text-sm text-gray-900 dark:text-white">No locations found</div>
                      ) : (
                        predictions.map((p) => {
                          console.log('Rendering prediction:', p.place_id, p.description);
                          return (
                            <button
                              key={p.place_id}
                              onClick={() => handleSuggestionSelect(p)}
                              className="w-full flex items-start gap-3 p-3 text-left hover:bg-gray-200 dark:hover:bg-black-100 border-b border-gray-300 dark:border-gray-700 last:border-0 transition-colors"
                            >
                              <MapPin className="h-5 w-5 text-gray-600 dark:text-gray-400 mt-1 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                  {p.structured_formatting?.main_text || p.description}
                                </p>
                                <p className="text-xs text-gray-700 dark:text-gray-300 truncate">
                                  {p.structured_formatting?.secondary_text || p.description}
                                </p>
                              </div>
                            </button>
                          )
                        })
                      )}
                    </div>
                  )}

                {searchError && (
                  <div className="absolute bottom-full mb-2 left-0 right-0 p-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm rounded-lg border border-red-300 dark:border-red-800 z-50">
                    {searchError}
                  </div>
                )}
              </div>
            )}

          {showPhoneInput && (
            <div className="max-w-3xl mx-auto">
              <CustomInput
                value={phoneNumberInput}
                onChange={(e) => {
                  setPhoneNumberInput(e.target.value);

                  if (currentStep === "pickup-phone") {
                    const myNumber = currentUser?.phone || currentUser?.user?.phone;
                    const normalizedMyNumber = myNumber?.replace(/^\+2340/, '+234');
                    const typed = e.target.value.trim();
                    const digits = typed.replace(/\D/g, '');

                    let formatted = typed;
                    if (typed.startsWith('+234')) {
                      formatted = typed.replace(/^\+2340/, '+234');
                    } else if (digits.length === 11 && digits.startsWith('0')) {
                      formatted = `+234${digits.substring(1)}`; // ← 0 prefix handled
                    } else if (digits.length === 10) {
                      formatted = `+234${digits}`; // ← no leading 0, just bare 10 digits
                    }

                    pickupPhoneMatchesUserPhone.current = formatted === normalizedMyNumber;
                  }
                }}

                placeholder="Enter phone number"
                showMic={false}
                className="placeholder:dark:text-gray-300 placeholder:text-gray-800"
                showIcons={false}
                send={() => {
                  if (phoneNumberInput.trim()) {
                    setMessages((prev) => prev.map((msg) =>
                      msg.hasUseMyNumberButton ? { ...msg, hasUseMyNumberButton: false } : msg
                    ));

                    if (currentStep === "pickup-phone") {
                      send(phoneNumberInput, "pickup-phone");
                    } else if (currentStep === "dropoff-phone") {
                      send(phoneNumberInput, "dropoff-phone");
                    }
                    setPhoneNumberInput("");
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
    </Onboarding>
  );
}